import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X, ArrowRightLeft, PackageOpen } from "lucide-react";

/**
 * MoveItemsModal — small confirmation dialog for the "Move selected items"
 * bulk action on the Items list toolbar.
 *
 * Presentation-only: parent owns the selection set and the per-item PATCH
 * loop. This component just collects a category id and hands it back via
 * `onConfirm`. Escape / backdrop click / X button all close cleanly.
 *
 * Props:
 *   open           boolean — modal visibility
 *   onClose()      called on Cancel / Escape / X / backdrop
 *   onConfirm(id)  called with the chosen category id (or "" for none). Parent
 *                  performs the batch PATCH and closes the modal.
 *   selectedCount  number — used in the header count line
 *   categories     [{ id, category_name, collection_id }] — populates dropdown
 *   isMobile       boolean — swaps the placeholder text between the full
 *                  "Move to category…" (desktop) and "Move…" (mobile)
 *   saving         boolean — disables inputs + shows spinner on the confirm btn
 */
export default function MoveItemsModal({
  open,
  onClose,
  onConfirm,
  selectedCount,
  categories,
  isMobile,
  saving,
}) {
  const [categoryId, setCategoryId] = useState("");

  // Reset dropdown selection every time the modal (re)opens.
  useEffect(() => {
    if (open) setCategoryId("");
  }, [open]);

  // Esc-to-close (only while open + not saving).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const confirm = () => {
    if (!categoryId) return;
    onConfirm?.(categoryId);
  };

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/55 backdrop-blur-sm vp-fade-in"
      data-testid="move-items-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-items-modal-title"
        className="w-full sm:max-w-md bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-t-2xl sm:rounded-lg shadow-xl flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <ArrowRightLeft
                size={14}
                className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
              />
              <h2
                id="move-items-modal-title"
                data-testid="move-items-modal-title"
                className="text-base font-semibold tracking-tight"
              >
                Move selected items to category
              </h2>
            </div>
            <p className="text-xs vp-muted">
              {selectedCount} item{selectedCount === 1 ? "" : "s"} selected
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="move-items-modal-close"
            aria-label="Close"
            className="vp-btn vp-btn-ghost vp-btn-sm !w-8 !h-8 !p-0 border border-[hsl(var(--border))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          {selectedCount === 0 ? (
            <div
              className="text-center py-6 vp-fade-in"
              data-testid="move-items-modal-empty"
            >
              <div className="mx-auto mb-4 w-12 h-12 rounded-2xl bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] flex items-center justify-center">
                <PackageOpen size={22} strokeWidth={1.75} />
              </div>
              <h3 className="text-base font-semibold tracking-tight mb-1.5">
                No items selected
              </h3>
              <p className="text-xs vp-muted max-w-xs mx-auto leading-relaxed">
                Select items first, then choose a category to move them.
              </p>
            </div>
          ) : (
            <>
              <label
                htmlFor="move-items-modal-category"
                className="block text-xs font-medium vp-muted"
              >
                Category
              </label>
              <select
                id="move-items-modal-category"
                data-testid="move-items-modal-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={saving}
                className="vp-select vp-select-sm vp-select-strong text-[13px] font-medium h-10 py-0 pl-3 pr-9 w-full text-[hsl(var(--text))] disabled:opacity-50"
              >
                <option value="" disabled>
                  {isMobile ? "Move…" : "Move to category…"}
                </option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.category_name}
                  </option>
                ))}
                <option
                  value="__UNCAT__"
                  className="italic text-gray-500 border-t border-gray-300 mt-1 pt-1"
                >
                  {isMobile ? "Uncategorised" : "— Uncategorised —"}
                </option>
              </select>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex justify-end gap-2">
          {selectedCount === 0 ? (
            <button
              type="button"
              onClick={onClose}
              data-testid="move-items-modal-empty-close"
              className="vp-btn vp-btn-primary vp-btn-sm"
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                data-testid="move-items-modal-cancel"
                className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={saving || !categoryId}
                data-testid="move-items-modal-confirm"
                className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                {saving ? "Moving…" : "Move items"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}
